import { Lightning, Router } from '@lightningjs/sdk'

import SpeechComp from '../../components/speechComp/speechComp'
import { getColor, grey960, transparent } from '../../constants/colors'
import { Images } from '../../constants/paths'
import CarouselsHandler from '../../pages/handlers/carouselsHandler'
import { getAsset } from '../../utils/image'
import { createItems, menuCollapsed, menuUncollapsed } from '../../utils/menuUtils'

const COLLAPSED_MENU_WIDTH = 176
const UNCOLLAPSED_MENU_WIDTH = 880
const COLLAPSED_LOGO_WIDTH = 32
const UNCOLLAPSED_LOGO_WIDTH = 281
const LOGO_HEIGHT = 29

const DEFAULT_INDEX = 1

export default class Menu extends SpeechComp {
  private _currentIndex = DEFAULT_INDEX
  private _selectedIndex = DEFAULT_INDEX

  static _template() {
    return {
      w: COLLAPSED_MENU_WIDTH,
      h: 1080,
      collision: true,
      BackgroundUncollapsed: {
        h: (h) => h,
        w: COLLAPSED_MENU_WIDTH,
        flex: {
          direction: 'row',
        },
        alpha: 0,
        Stop0: {
          rect: true,
          h: (h) => h,
          w: (w) => w * 0.606822,
          colorLeft: getColor(grey960),
          colorRight: getColor(grey960, 0.919348),
        },
        Stop1: {
          rect: true,
          h: (h) => h,
          w: (w) => w * 0.162955,
          colorLeft: getColor(grey960, 0.919348),
          colorRight: getColor(grey960, 0.696217),
        },
        Stop2: {
          rect: true,
          h: (h) => h,
          w: (w) => w * 0.230213,
          colorLeft: getColor(grey960, 0.696217),
          colorRight: transparent,
        },
      },
      BackgroundCollapsed: {
        h: (h) => h,
        w: COLLAPSED_MENU_WIDTH,
        flex: {
          direction: 'row',
        },
        alpha: 1,
        Stop0: {
          rect: true,
          h: (h) => h,
          w: (w) => w * 0.6757,
          color: getColor(grey960),
        },
        Stop1: {
          rect: true,
          h: (h) => h,
          w: (w) => w * 0.1412,
          colorLeft: getColor(grey960),
          colorRight: getColor(grey960, 0.759741),
        },
        Stop2: {
          rect: true,
          h: (h) => h,
          w: (w) => w * 0.0854,
          colorLeft: getColor(grey960, 0.759741),
          colorRight: getColor(grey960, 0.452188),
        },
        Stop3: {
          rect: true,
          h: (h) => h,
          w: (w) => w * 0.0977,
          colorLeft: getColor(grey960, 0.452188),
          colorRight: transparent,
        },
      },
      Container: {
        w: (w) => w,
        h: (h) => h,
        rect: true,
        color: transparent,
        flex: {
          direction: 'column',
        },
        LogoContainer: {
          y: 32,
          x: 59,
          h: LOGO_HEIGHT,
          w: 281,
          LogoCollapsed: {
            visible: true,
            texture: Lightning.Tools.getSvgTexture(
              getAsset(Images.LOGO_SMALL),
              COLLAPSED_LOGO_WIDTH,
              LOGO_HEIGHT,
            ),
          },
          LogoUncollapsed: {
            visible: false,
            texture: Lightning.Tools.getSvgTexture(
              getAsset(Images.LOGO_LARGE),
              UNCOLLAPSED_LOGO_WIDTH,
              LOGO_HEIGHT,
            ),
          },
        },
        InnerContainer: {
          x: 32,
          w: (w) => w,
          h: (h) => h,
          flex: {
            direction: 'column',
            justifyContent: 'center',
          },
          collision: true,
        },
      },
    }
  }

  get sectionText() {
    return (
      'Main Menu ' + this.innerContainer.children.length + ' Items, scroll up and down to navigate'
    )
  }

  get logoCollapsed() {
    return this.tag('LogoCollapsed')
  }

  get logoUncollapsed() {
    return this.tag('LogoUncollapsed')
  }

  get innerContainer() {
    return this.tag('InnerContainer')
  }

  get container() {
    return this.tag('Container')
  }

  _init() {
    const menuItems = createItems()
    this.innerContainer.children = menuItems
  }

  _getFocused() {
    return this.innerContainer.children[this._currentIndex]
  }

  _focus() {
    this.toggleMenu(true)
    this.application.emit(menuUncollapsed)
  }

  _unfocus() {
    this._currentIndex = this._selectedIndex

    this.toggleMenu(false)
    this.application.emit(menuCollapsed)
  }

  _handleDown() {
    if (this._currentIndex < this.innerContainer.children.length - 1) {
      this._currentIndex++
    }
  }

  _handleUp() {
    if (this._currentIndex > 0) {
      this._currentIndex--
    }
  }

  _handleLeft() {
    // Prevent menu from focusing page
  }

  _handleRight() {
    this.unfocusMenu()
  }

  unfocusMenu() {
    if (Router.getActivePage().widgets.modal.visible) {
      Router.focusWidget('Modal')
    } else {
      Router.focusPage()
    }
  }

  _handleEnter() {
    if (this._selectedIndex !== this._currentIndex) {
      this.innerContainer.childList.getAt(this._selectedIndex).unselectItem()
      this._selectedIndex = this._currentIndex
    }

    this.innerContainer.childList.getAt(this._selectedIndex).selectItem()

    this._unfocus()
  }

  _handleBack(event) {
    event.preventDefault()
    ;(Router.getActivePage() as any)._setState('InBackgroundByModal')
    Router.focusWidget('ExitWindow')
  }

  selectItem(index = DEFAULT_INDEX) {
    // Unselect previous menu item
    this.innerContainer.childList.getAt(this._selectedIndex).unselectItem()

    // Update new selected item
    this._selectedIndex = index
    this._currentIndex = index
    const menuItem = this.innerContainer.childList.getAt(this._selectedIndex)
    menuItem.isSelected = true
    menuItem.focusItem()
  }

  toggleMenu(toggle: boolean) {
    this.logoCollapsed.visible = !toggle
    this.logoUncollapsed.visible = toggle

    this.patch({
      smooth: {
        w: toggle ? UNCOLLAPSED_MENU_WIDTH : COLLAPSED_MENU_WIDTH,
      },
      BackgroundUncollapsed: {
        alpha: toggle ? 1 : 0,
        smooth: {
          w: toggle ? UNCOLLAPSED_MENU_WIDTH : COLLAPSED_MENU_WIDTH,
        },
      },
      BackgroundCollapsed: {
        alpha: toggle ? 0 : 1,
        smooth: {
          w: toggle ? UNCOLLAPSED_MENU_WIDTH : COLLAPSED_MENU_WIDTH,
        },
      },
    })
  }

  isAnotherWidgetVisible() {
    const widgets = Router.getActivePage().widgets

    return Object.values(widgets).some((widget: any) => {
      return widget.ref !== 'Menu' && widget.ref !== 'Modal' && widget.visible
    })
  }

  _handleUnhover({ ref }) {
    if (this.isAnotherWidgetVisible()) {
      return
    }

    // 'If this root component, the "Menu" component gets unhovered, then...' because we get this event for the
    // Container and InnerContainer as well, which we don't care about
    if (ref === this.ref) {
      const activePageComponent = Router.getActivePage()
      if (activePageComponent instanceof CarouselsHandler) {
        activePageComponent.toggleCurrentCarouselLeftNavigationPointer(true)
      }

      this.unfocusMenu()
    }
  }

  $updateFocusByHover({ itemIndex }) {
    if (this.isAnotherWidgetVisible()) {
      return
    }

    this._currentIndex = itemIndex

    if (this.logoCollapsed.visible) {
      const activePageComponent = Router.getActivePage()
      if (activePageComponent instanceof CarouselsHandler) {
        activePageComponent.toggleCurrentCarouselLeftNavigationPointer(false)
      }

      Router.focusWidget('Menu')
      this._focus()
    }

    this._refocus()
  }

  $onMenuItemClicked() {
    if (this.isAnotherWidgetVisible()) {
      return
    }

    this._handleEnter()
  }
}
